<template>
  <b-modal
    v-model="showModal"
    :title="
      isEditMode
        ? 'Edit Extra Payment Category'
        : 'Create Extra Payment Category'
    "
    size="lg"
    hide-footer
    @hidden="handleModalHidden"
  >
    <validation-observer ref="formRef">
      <b-form @submit.prevent="handleSubmit">
        <!-- Name Field -->
        <b-form-group label="Name *">
          <validation-provider
            #default="{ errors }"
            name="name"
            rules="required"
          >
            <b-form-input
              v-model.trim="form.name"
              :state="errors.length > 0 ? false : null"
              placeholder="Enter category name"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group label="Benefit En *">
          <validation-provider
            #default="{ errors }"
            name="benefit_en"
            rules="required"
          >
            <b-form-input
              v-model.trim="form.benefit_en"
              :state="errors.length > 0 ? false : null"
              placeholder="Enter benefit en"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group label="Benefit Ar *">
          <validation-provider
            #default="{ errors }"
            name="benefit_ar"
            rules="required"
          >
            <b-form-input
              v-model.trim="form.benefit_ar"
              :state="errors.length > 0 ? false : null"
              placeholder="Enter benefit ar"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Group Segment Name Field -->
        <b-form-group label="Group Segment Name">
          <validation-provider #default="{ errors }" name="group_segment_name">
            <v-select
              v-model="form.group_segment_name"
              :options="groupsList"
              :reduce="(val) => val.id"
              label="name"
              placeholder="Search and select a group (optional)"
              :clearable="true"
              @search="fetchGroups"
            >
              <template #no-options>
                Type to search for groups...
              </template>
            </v-select>
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Code Field -->
        <b-form-group label="Code">
          <validation-provider
            #default="{ errors }"
            name="code"
            rules="required"
          >
            <b-form-input
              v-model.trim="form.code"
              :state="errors.length > 0 ? false : null"
              placeholder="Enter category"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Currency Field -->
        <b-form-group label="Currency">
          <validation-provider #default="{ errors }" name="currency">
            <b-form-input
              v-model.trim="form.currency"
              :state="errors.length > 0 ? false : null"
              placeholder="Enter currency (optional)"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Amount Field -->
        <b-form-group label="Amount *">
          <validation-provider
            #default="{ errors }"
            name="amount"
            rules="required"
          >
            <b-form-input
              v-model.number="form.amount"
              type="number"
              :state="errors.length > 0 ? false : null"
              placeholder="Enter amount"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Active/Inactive Switch (Edit Mode Only) -->
        <b-form-group v-if="isEditMode" label="Status">
          <b-form-checkbox v-model="form.is_active" switch>
            {{ form.is_active ? "Active" : "Inactive" }}
          </b-form-checkbox>
        </b-form-group>

        <!-- Custom Buttons -->
        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="outline-secondary"
            class="mr-2"
            @click="handleCancel"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            :disabled="isSubmitting"
            @click="handleSubmit"
          >
            <b-spinner v-if="isSubmitting" small class="mr-1" />
            {{
              isSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                ? "Update"
                : "Save"
            }}
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import { ref, reactive, watch, nextTick } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import {
  BFormGroup,
  BFormInput,
  BForm,
  BButton,
  BSpinner,
  BModal,
  BFormCheckbox,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { debounce } from "lodash";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import axiosIns from "@/libs/axios";

export default {
  name: "ExtraPaymentForm",
  components: {
    ValidationObserver,
    ValidationProvider,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BSpinner,
    BModal,
    BFormCheckbox,
    vSelect,
  },
  props: {
    category: {
      type: Object,
      default: null,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const toast = useToast();
    const formRef = ref(null);
    const groupsList = ref([]);
    const isSubmitting = ref(false);
    const showModal = ref(false);

    const form = reactive({
      name: "",
      benefit_en: "",
      benefit_ar: "",
      group_segment_name: "",
      code: "",
      currency: "",
      amount: 0,
      is_active: true,
    });

    const fetchGroups = debounce(async (search, loading) => {
      if (!search) {
        groupsList.value = [];
        return;
      }
      loading(true);

      try {
        const result = await axiosIns.get("/groups", {
          params: {
            name: search,
          },
        });
        const { data } = result.data.data;
        groupsList.value = data;
      } catch (error) {
        console.error("Error fetching groups:", error);
        groupsList.value = [];
      } finally {
        loading(false);
      }
    }, 300);

    const resetForm = () => {
      form.name = "";
      form.benefit_en = "";
      form.benefit_ar = "";
      form.group_segment_name = "";
      form.code = "";
      form.currency = "";
      form.amount = 0;
      form.is_active = true;

      nextTick(() => {
        if (formRef.value) {
          formRef.value.reset();
        }
      });
    };

    const handleSubmit = async () => {
      const isValid = await formRef.value.validate();
      if (!isValid) return;

      isSubmitting.value = true;
      try {
        if (props.isEditMode && props.category) {
          // Call API to update category
          const response = await axiosIns.put(
            `/bills/extra/categories/${props.category.id}`,
            { ...form }
          );
          if (response.data) {
            toast.success("Category updated successfully");
            showModal.value = false;
            resetForm();
            // Emit success event to parent for list reload
            emit("success");
          }
        } else {
          // Call API to create category
          const response = await axiosIns.post("/bills/extra/categories", {
            ...form,
          });
          if (response.data) {
            toast.success("Category created successfully");
            showModal.value = false;
            resetForm();
            // Emit success event to parent for list reload
            emit("success");
          }
        }
      } catch (error) {
        console.error("Error saving category:", error);
        const action = props.isEditMode ? "update" : "create";
        toast.error(
          error.response?.data?.message || `Failed to ${action} category`
        );
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleCancel = () => {
      showModal.value = false;
      resetForm();
    };

    const handleModalHidden = () => {
      if (!props.isEditMode) {
        resetForm();
      }
    };

    const openModal = () => {
      showModal.value = true;
    };

    const validate = async () => formRef.value.validate();

    const getFormData = () => ({ ...form });

    // Watch for category changes
    watch(
      () => props.category,
      (newCategory) => {
        if (newCategory && props.isEditMode) {
          form.benefit_en = newCategory.benefit_en || "";
          form.benefit_ar = newCategory.benefit_ar || "";
          form.name = newCategory.name || "";
          form.group_segment_name = newCategory.group_segment_name || "";
          form.code = newCategory.code || "";
          form.currency = newCategory.currency || "";
          form.amount = newCategory.amount || 0;
          form.is_active =
            newCategory.is_active !== undefined ? newCategory.is_active : true;
        } else {
          resetForm();
        }
      },
      { immediate: true, deep: true }
    );

    return {
      required,
      formRef,
      form,
      groupsList,
      isSubmitting,
      showModal,
      fetchGroups,
      handleSubmit,
      handleCancel,
      handleModalHidden,
      openModal,
      validate,
      getFormData,
      resetForm,
    };
  },
};
</script>
